import React, {useEffect, useState} from "react"
import styled from "styled-components"
import {api} from "@indebted/api"
import {fmt} from "@indebted/fmt"
import {ErrorPage} from "@indebted/components/Error"
import {LoadingPage} from "@indebted/components/Loading"
import {Header, HeaderFormControl} from "@indebted/components/Header"
import {Metric} from "@indebted/components/Metric"
import {TextFilter} from "@indebted/components/Filters"
import {Card, Grid, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow} from "@material-ui/core"
import {useLocation, useNavigate} from "@reach/router"

import {Row} from "./Row"

function ClientFiles() {
	const navigate = useNavigate()
	const location = useLocation()
	const urlParams = new URLSearchParams(location.search)

	const [data, setData] = useState()
	const [error, setError] = useState()
	const [filters, setFilters] = useState({
		ClientID: urlParams.get("ClientID") || "",
		Directory: urlParams.get("Directory") || "",
		FileType: urlParams.get("FileType") || "All Files",
		Filename: urlParams.get("Filename") || "",
	})

	useEffect(search, [navigate, filters])

	function search() {
		setData(null)
		navigate(`/client-files?${new URLSearchParams(filters).toString()}`, {replace: true})
		api.clientFiles.search(filters).then(setData).catch(setError)
	}

	if (error) return <ErrorPage message={error.Message} />
	if (!data) return <LoadingPage />

	const m = data.Metrics

	function successColor(rate) {
		if (rate == 0) return "darkGrey"
		if (rate > 0.8) return "green"
		if (rate > 0.6) return "orange"
		return "red"
	}

	function handleClientChange(e) {
		setFilters({
			...filters,
			ClientID: e.target.value,
		})
	}

	function handleDirectoryChange(e) {
		setFilters({
			...filters,
			Directory: e.target.value,
		})
	}

	function handleFileTypeChange(e) {
		setFilters({
			...filters,
			FileType: e.target.value,
		})
	}

	function handleFilenameChange(e) {
		setFilters({
			...filters,
			Filename: e.target.value,
		})
	}

	return (
		<>
			<Header title="Client Files">
				<HeaderFormControl>
					<Select displayEmpty={true} value={filters.ClientID} onChange={handleClientChange}>
						<MenuItem value="" disabled>
							Select Client
						</MenuItem>
						{data.Filters.Clients.map((client, i) => (
							<MenuItem key={i} value={client.ID}>
								{client.Name}
							</MenuItem>
						))}
					</Select>
				</HeaderFormControl>
				<HeaderFormControl>
					<Select displayEmpty={true} value={filters.Directory} onChange={handleDirectoryChange}>
						<MenuItem value="">All Directories</MenuItem>
						{data.Filters.Directories.map((directory, i) => (
							<MenuItem key={i} value={directory}>
								{directory}
							</MenuItem>
						))}
					</Select>
				</HeaderFormControl>
				<HeaderFormControl>
					<Select displayEmpty={true} value={filters.FileType} onChange={handleFileTypeChange}>
						{data.Filters.FileTypes.map((fileType, i) => (
							<MenuItem key={i} value={fileType}>
								{fileType}
							</MenuItem>
						))}
					</Select>
				</HeaderFormControl>
				<TextFilter
					autoFocus={true}
					value={filters.Filename}
					placeholder="Filename"
					onEnterKeyDown={handleFilenameChange}
				/>
			</Header>

			<Grid container spacing={3}>
				<Grid item xs={3}>
					<Metric
						color={successColor(m["12h"].SuccessRate)}
						title={fmt.percent(m["12h"].SuccessRate)}
						description={`${fmt.integer(m["12h"].CSVFiles)} files in the last 12 hours`}
					/>
				</Grid>
				<Grid item xs={3}>
					<Metric
						color={successColor(m["7d"].SuccessRate)}
						title={fmt.percent(m["7d"].SuccessRate)}
						description={`${fmt.integer(m["7d"].CSVFiles)} files in the last 7 days`}
					/>
				</Grid>
				<Grid item xs={3}>
					<Metric
						color={successColor(m["30d"].SuccessRate)}
						title={fmt.percent(m["30d"].SuccessRate)}
						description={`${fmt.integer(m["30d"].CSVFiles)} files in the last 30 days`}
					/>
				</Grid>
				<Grid item xs={3}>
					<Metric
						color={successColor(m["90d"].SuccessRate)}
						title={fmt.percent(m["90d"].SuccessRate)}
						description={`${fmt.integer(m["90d"].CSVFiles)} files in the last 90 days`}
					/>
				</Grid>
			</Grid>

			<Card>
				<TableNoWrap>
					<TableHead>
						<TableRow>
							<TableCell>Last Modified</TableCell>
							<TableCell>Client</TableCell>
							<TableCell>Directory</TableCell>
							<MetricCell>Size</MetricCell>
							<TableCell>File</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.Files.map((file, i) => (
							<Row key={i} file={file} />
						))}
					</TableBody>
				</TableNoWrap>
			</Card>
		</>
	)
}

const TableNoWrap = styled(Table)`
	white-space: nowrap;
`

const MetricCell = styled(TableCell)`
	text-align: right;
`

export {ClientFiles}
